<template>
    <div class="page-wrapper ticket-wrapper ticket-manage-wrapper">
        <div class="container-fluid">
            <div class="breadcrumbwrapper">
                <breadcrumb :items="breadcrumbItems" />
            </div>
            <div class="ticket-card p-4 mt-3 ticket-list-wrapper" v-if="ticketData">
                <div class="ticket-header d-flex align-items-center gap-3 flex-wrap ">
                    <div class="ticket-item d-flex align-items-center gap-1">
                        <span class="itemtitle ">Category :</span>
                        <span class="itemvalue fw-semibold clr-black">{{ ticketData.category.name }}</span>

                    </div>
                    <div class="ticket-item d-flex align-items-center gap-1">
                        <span class="itemtitle">Status :</span>
                        <div
                            :class="['d-inline-flex align-items-center justify-content-center badgecontainer', getStatusClass(ticketData.status)]">
                            <span :class="['badge-dot', ticketData.status === 1 ? 'open' : 'closed']"></span>
                            <span>{{ getStatusText(ticketData.status) }}</span> 
                        </div>
                    </div>
                    <div class="ticket-item d-flex align-items-center gap-1">
                        <span class="itemtitle">Opened Date :</span>
                        <span class="itemvalue fw-semibold clr-black">{{ formatDate(ticketData.opened_date)
                            }}</span>
                    </div>
                </div>
               <button v-if="ticketData.status === 1" class="btn btn-outline-danger close-ticket-btn ms-auto d-block"
                    @click="handleCloseTicket" :disabled="isClosing">
                    <i class="fas fa-times-circle me-1"></i>
                    {{ isClosing ? 'Closing...' : 'Close Ticket' }}
                </button>
                <div class="complaint_section mt-4 pt-2">
                    <div class="subjectwrapper">
                        <h6 class="complaintHeader fw-bold fs-16 clr-title">Subject</h6>
                        <div class="complaintContent fs-16">
                            {{ ticketData.title }}
                        </div>
                    </div>
                    <hr>
                    <div class="descriptionwrapper">
                        <h6 class="complaintHeader fw-bold fs-16 clr-title">Description</h6>
                        <div class="complaintContent fs-16" v-html="ticketData.details">
                        </div>
                    </div>
                </div>
                <div class="tab-wrapper mt-4">
                    <div class="tab-wrapper-btns d-inline-flex align-items-center gap-1 flex-wrap">
                        <button v-for="(tab, index) in tabs" :key="index"
                            class="tab-btn border-0 d-flex align-items-center gap-2"
                            :class="{ active: activeTab === tab }" @click="setActiveTab(tab)">
                            <i :class="tab.icon"></i> {{ tab.title }}
                        </button>
                    </div>
                    <div class="tab-content mt-4">
                        <component :is="activeTab.component" v-if="activeTab.component" :ticket-data="ticketData" 
                        :add-comment="ticketData.status === 1"
                        :format-date="formatDate"/>
                    </div>
                </div>
            </div>

            <!-- Loading state -->
            <div v-else-if="loading" class="text-center p-4">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>

            <!-- Error state -->
            <div v-else class="alert alert-danger" role="alert">
                {{ error }}
            </div>
        </div>

    </div>
</template>

<script>
import TicketIssueComment from './TicketIssueComment.vue';
import TicketIssueDocument from './TicketIssueDocument.vue';
import TickerSubmitterDetail from './TickerSubmitterDetail.vue';
import axios from 'axios';
import Helper from '@/resource/Helper';

export default {
    components: {
        TicketIssueComment,
        TicketIssueDocument,
        TickerSubmitterDetail
    },
    name: "TicketIssueDetail",
    data() {
        return {
            breadcrumbItems: [{ title: "Manage Tickets", active: false, path: { name: "ManageTicket" } }, { title: "Issue Detail", active: false }],
            statusOptions: [
                { value: 1, text: 'Open' },
                { value: 0, text: 'Closed' }
            ],
            tabs: [
                {
                    title: "Comments",
                    icon: "fa-solid fa-comments",
                    component: TicketIssueComment,
                },
                {
                    title: "Issue Documents",
                    icon: "fa-solid fa-file",
                    component: TicketIssueDocument,
                },
                {
                    title: "Submitter Details",
                    icon: "fa-solid fa-user",
                    component: TickerSubmitterDetail,
                },

            ],
            activeTab: null,
            ticketData: null,
            loading: false,
            error: null,
            issueId: null,
            isClosing: false,
        };
    },
    watch: {
        '$route'(to, from) {
            // Watch for route changes to handle navigation between different ticket details
            if (to.params.ticketId !== from.params.ticketId) {
                this.issueId = to.params.ticketId;
                this.fetchTicketDetails();
            }
        }
    },
    created() {
        this.activeTab = this.tabs[0];
        this.issueId = this.$route.params.ticketId; // Get from route params or default
        if (!this.issueId) {
            this.error = 'No ticket ID provided';
            return;
        }
        this.fetchTicketDetails();
    },
    methods: {
        async fetchTicketDetails() {
            let loader = this.$loading.show();
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_TICKETING_URL}v2/agent/${this.issueId}/show`,
                    {
                        headers: {
                            'X-Authorization': Helper.messageCenterToken(),
                            'X-Ticketing-Token': Helper.ticketingToken()
                        }
                    }
                );
                if (response?.data?.data) {
                    this.ticketData = response?.data?.data;
                } else {
                    this.error = 'No ticket data found';
                }
                this.loading = false;
            } catch (error) {
                console.error('Error fetching ticket details:', error);
                this.error = 'Failed to load ticket details. Please try again.';
            } finally {
                loader.hide();
                this.loading = false;
            }
        },
        handleCloseTicket() {
            this.$confirm({
                title: 'Close Ticket',
                message: 'Are you sure you want to close this ticket?',
                button: { no: 'No', yes: 'Yes' },
                callback: async (confirm) => {
                    if (!confirm) return;

                    this.isClosing = true;
                    let loader = this.$loading.show();

                    try {
                        const url = `${process.env.VUE_APP_TICKETING_URL}v2/agent/issues/${this.issueId}/close`;
                        const response = await axios.put(
                            url,
                            { agent_id: this.$store.getters.repId },
                            { headers: { 'X-Ticketing-Token': Helper.ticketingToken() } }
                        );

                        this.$notify(response.data.message || 'Ticket closed successfully.');


                        await this.fetchTicketDetails();

                    } catch (err) {

                        const msg = err?.response?.data?.message || 'Failed to close ticket. Please try again.';
                        this.$notify(msg, 'error');
                    } finally {
                        this.isClosing = false;
                        loader.hide();
                    }
                }
            });
        },

        setActiveTab(tab) {
            this.activeTab = tab;
        },

        getStatusText(status) {
            const statusOption = this.statusOptions.find(option => option.value === status);
            return statusOption ? statusOption.text : 'Unknown';
        },
        getStatusClass(status) {
            // status 1 = Open (green), status 0 = Closed (grey)
            return status === 1 ? 'issue-status' : 'closeissue-status';
        },

        async updateStatus(newStatus) {
            try {
                this.ticketData.issue.status = newStatus;
            } catch (error) {
                console.error('Error updating status:', error);
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';

            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });
        }
    }
};
</script>

<style src="@/assets/css/ticketing.css"></style>
<style scoped>
.ticket-wrapper .tab-wrapper-btns {
    border-radius: 30px;
    padding: 1px;
}

.ticket-wrapper .tab-wrapper .tab-btn {
    background-color: #fff;
    padding: 14px 24px;
    border-radius: 30px;

}

.ticket-wrapper .tab-wrapper .tab-btn.active,
.ticket-wrapper .tab-wrapper .tab-btn.active .fa-user {
    background-color: var(--rep-primary-clr);
    color: #fff;
}

.ticket-wrapper .tab-wrapper .tab-btn .fa-user {
    color: #000000A6;
    background: transparent;
}

.spinner-border {
    width: 3rem;
    height: 3rem;
}

:root {
    --rep-bold-green-clr: #3bb54a;
    --rep-bold-grey-clr: #778699;
}

.ticket-list-wrapper .issue-status {
    background-color: var(--rep-bold-green-clr);
    color: #fff;
}

.ticket-list-wrapper .closeissue-status {
    background-color: var(--rep-bold-grey-clr);
    color: #fff;
}

.is-opened {
    color: var(--rep-bold-green-clr);
}

.complaintContent {
  max-width: 100%;
  overflow: hidden;
}

.complaintContent :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 10px 0;
  border-radius: 4px;
}
</style>